import {
    collection,
    doc,
    addDoc,
    updateDoc,
    getDocs,
    query,
    where,
    deleteDoc,
    Timestamp,
    serverTimestamp,
    orderBy,
    getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Generate a random string of a given length
 */
const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
        result += chars.charAt(array[i] % chars.length);
    }
    return result;
};

/**
 * Hash a string using SHA-256
 */
const hashString = async (str: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export interface ApiKey {
    id: string;
    name: string;
    accessTokenHash: string; // Store hash instead of plaintext
    keyPreview: string;     // Store a preview like 'sm_abc123...' for the user to identify
    userId: string;
    workspaceId?: string | null;
    createdAt: Timestamp;
    lastUsedAt?: Timestamp | null;
    expiresAt?: Timestamp | null;
    revokedAt?: Timestamp | null;
    type: 'personal';
    isActive: boolean;
    dailySendLimit?: number | null; // optional per-token cap
}

/** Maximum active (non-revoked) personal tokens allowed per workspace. */
const MAX_TOKENS_PER_WORKSPACE = 5;

class ApiKeyService {
    /**
     * Generate a new personal access token
     * ⚠️ SECURITY: The plaintext token is returned ONLY ONCE upon creation.
     */
    async generateApiKey(userId: string, name: string, workspaceId?: string): Promise<{ id: string; token: string }> {
        // Enforce per-workspace token cap before creating
        if (workspaceId) {
            const activeQuery = query(
                collection(db, 'oauthTokens'),
                where('userId', '==', userId),
                where('workspaceId', '==', workspaceId),
                where('type', '==', 'personal'),
                where('isActive', '==', true)
            );
            const snap = await getDocs(activeQuery);
            if (snap.size >= MAX_TOKENS_PER_WORKSPACE) {
                throw new Error(
                    `Token limit reached: a workspace may have at most ${MAX_TOKENS_PER_WORKSPACE} active API tokens. Revoke an existing token before creating a new one.`
                );
            }
        }

        // Generate a secure random token
        const rawToken = `sm_${generateRandomString(32)}`;
        const tokenHash = await hashString(rawToken);
        
        // Create a preview for the UI
        const keyPreview = `${rawToken.substring(0, 7)}...${rawToken.substring(rawToken.length - 4)}`;

        const docRef = await addDoc(collection(db, 'oauthTokens'), {
            accessTokenHash: tokenHash,
            keyPreview,
            userId,
            name,
            type: 'personal',
            workspaceId: workspaceId || null,
            createdAt: serverTimestamp(),
            isActive: true,
            revokedAt: null,
            dailySendLimit: null,
        });

        return {
            id: docRef.id,
            token: rawToken
        };
    }

    /**
     * Get all API keys for a user
     */
    async getUserApiKeys(userId: string, workspaceId?: string): Promise<ApiKey[]> {
        const q = query(
            collection(db, 'oauthTokens'),
            where('userId', '==', userId),
            where('type', '==', 'personal')
        );

        const snapshot = await getDocs(q);
        const keys = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        })) as ApiKey[];

        if (!workspaceId) {
            return keys;
        }

        return keys.filter(
            (key) => !key.workspaceId || String(key.workspaceId) === String(workspaceId)
        );
    }

    /**
     * Soft-revoke an API key — sets isActive=false and records revokedAt.
     * The document is preserved for audit history.
     */
    async revokeApiKey(keyId: string): Promise<void> {
        await updateDoc(doc(db, 'oauthTokens', keyId), {
            isActive: false,
            revokedAt: serverTimestamp(),
        });
    }

    /**
     * Hard-delete an API key document (admin-only, no audit trail).
     * Prefer revokeApiKey for normal use.
     */
    async deleteApiKey(keyId: string): Promise<void> {
        await deleteDoc(doc(db, 'oauthTokens', keyId));
    }

    /**
     * Set a per-token daily send limit. Pass null to remove the cap.
     */
    async setDailySendLimit(keyId: string, limit: number | null): Promise<void> {
        await updateDoc(doc(db, 'oauthTokens', keyId), { dailySendLimit: limit ?? null });
    }

    /**
     * Get all API keys (for admin). Includes revoked tokens so the audit trail is visible.
     */
    async getAllApiKeys(): Promise<ApiKey[]> {
        const q = query(
            collection(db, 'oauthTokens'),
            where('type', '==', 'personal')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        })) as ApiKey[];
    }

    /**
     * Verify an API key against its hash
     */
    async verifyApiKey(rawToken: string, storedHash: string): Promise<boolean> {
        const incomingHash = await hashString(rawToken);
        return incomingHash === storedHash;
    }
}

export const apiKeyService = new ApiKeyService();
